import isEqual from 'react-fast-compare';

import {addOrReplaceAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_INPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {
  AnalysisRightSidebarItem,
  AnalysisRightSidebarItemType,
} from 'analysis-tool/right-sidebar/RightSidebarTypes';
import {getPersistedChartViewConfig} from 'analysis-tool/ui/charting/getPersistedVisualizationConfig';
import {AnalysisMainViewItem, AnalysisMainViewItemType} from 'analysis-tool/ui/main-view/MainViewTypes';
import {assert} from 'Asserts';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import getVisualizationType from 'insights/analytics-visualizations/data/getVisualizationType';
import {
  VisualizationConfig,
  VisualizationType,
} from 'insights/universal-blocks/SharedUniversalBlockViewTypes';
import {updateAndRunPipelineStep} from 'pipelines/network/PipelineDAO';
import {
  PipelineGraphQL,
  PipelineStep,
  PipelineStepConfig,
  pipelineStepIsStepType,
} from 'pipelines/PipelineTypes';

export function getNewStepTypeFromVisualizationConfig(
  visualizationConfig: VisualizationConfig
): PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW {
  return getVisualizationType(visualizationConfig) === VisualizationType.TABLE
    ? PipelineStepType.TABLE_VIEW
    : PipelineStepType.CHART_VIEW;
}

export function hasStepConfigChanged(
  step: PipelineStep<PipelineStepType.CHART_VIEW>,
  newStepConfig: PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>['chartConfig']
): boolean {
  const config = step.config as PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>;

  return !isEqual(config.chartConfig, newStepConfig);
}

/** @package */
export function getChartConfig(
  visualizationConfig: VisualizationConfig | undefined,
  newChartConfig: PipelineStepConfig<PipelineStepType.CHART_VIEW, 0> | undefined
): PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>['chartConfig'] {
  const chartConfig = visualizationConfig
    ? getPersistedChartViewConfig(visualizationConfig)
    : newChartConfig?.chartConfig;
  assert(chartConfig != null, 'Expected chart config to be provided');
  return chartConfig;
}

interface SaveChartOrTableParameters {
  analysis: PipelineGraphQL;
  originalChartOrTableStep: PipelineStep<PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW>;
  newChartConfig?: PipelineStepConfig<PipelineStepType.CHART_VIEW, 0>;
  newVisualizationConfig?: VisualizationConfig;
  /** Explicitly specify the new step type. When provided, this overrides the type derived from newVisualizationConfig. */
  newStepType?: PipelineStepType.CHART_VIEW | PipelineStepType.TABLE_VIEW;
  inputDataFrameId: string;
  onChangeSelectedRightSidebarItem: (newSelectedRightSidebarItem: AnalysisRightSidebarItem | null) => void;
  onChangeSelectedItem: (newSelectedItem: AnalysisMainViewItem | null) => void;
  selectedRightSidebarItem: AnalysisRightSidebarItem<AnalysisRightSidebarItemType.EDIT_VIEW>;
}

/**
 * Save updates to chart and table view steps.
 *
 * Users are able to switch chart view steps to table view steps and vice versa. This means when a user updates
 * those steps, we might need to call addOrReplaceAndRunAnalysisStep instead of updateAndRunPipelineStep, because
 * we do not allow updating the step type.
 *
 * Another wrinkle is that some charts have newVisualizationConfig, but new charts do not. VisualizationConfig is
 * a data model generated by charts used in dashboards, but new chart types (like box plot) do not have it. We
 * are using the same form component that the main view does for the older charts, VisualizationConfigEditor,
 * but this is temporary. When we redo the sidebar for those charts, we will not need VisualizationConfig anymore
 * and we can simplify this function.
 *
 * TODO(BNCH-119690): After we remove VisualizationConfigEditor, remove the newVisualizationConfig parameter from
 * this function.
 *
 * @param analysis
 * @param originalChartOrTableStep - The step to be updated.
 * @param newChartConfig - The new chart config to be saved.
 * @param newVisualizationConfig - The new visualization config to be used.
 * @param newStepType - Explicitly specify the new step type. When provided, this overrides
 *   the type derived from newVisualizationConfig. Used by the chart type switcher to change
 *   between CHART_VIEW and TABLE_VIEW without a VisualizationConfig.
 * @param inputDataFrameId - The id of the input data frame.
 * @param onChangeSelectedRightSidebarItem
 * @param onChangeSelectedItem
 */
export async function saveChartOrTable({
  analysis,
  originalChartOrTableStep,
  newChartConfig,
  newVisualizationConfig,
  newStepType,
  inputDataFrameId,
  selectedRightSidebarItem,
  onChangeSelectedRightSidebarItem,
  onChangeSelectedItem,
}: SaveChartOrTableParameters): Promise<void> {
  let newStep: GetAnalysisPipelineStep | undefined;

  // Use the explicitly provided step type if available. Otherwise, derive it from the visualization config.
  // If neither is provided, default to CHART_VIEW (legacy behavior for analysis-only chart types).
  const stepType =
    newStepType ??
    (newVisualizationConfig
      ? getNewStepTypeFromVisualizationConfig(newVisualizationConfig)
      : PipelineStepType.CHART_VIEW);

  const isReplacingStep = stepType !== originalChartOrTableStep.stepType;

  assert(
    stepType === PipelineStepType.TABLE_VIEW || newChartConfig != null,
    'chartConfig must be provided if stepType is CHART_VIEW'
  );
  // Temporarily set isUpdating to true, because the routers can get into a state where the analysis is not updated but the
  // view reference is.
  // We only need to do this when replacing the step, because the viewReference is otherwise the same.
  if (isReplacingStep) {
    onChangeSelectedItem({
      type: AnalysisMainViewItemType.DATA_VIEW,
      isUpdating: true,
      viewReference: selectedRightSidebarItem.viewReference,
    });
    onChangeSelectedRightSidebarItem({...selectedRightSidebarItem, isUpdating: true});
  }
  if (isReplacingStep && pipelineStepIsStepType(PipelineStepType.TABLE_VIEW, 0, originalChartOrTableStep)) {
    // In this case a user is updating a table view step into a chart view step.
    newStep = await addOrReplaceAndRunAnalysisStep(
      analysis.id,
      {
        stepType: PipelineStepType.CHART_VIEW,
        stepVersion: 0,
        stepConfig: {
          chartConfig: getChartConfig(newVisualizationConfig, newChartConfig),
          chartName: originalChartOrTableStep.config!.viewName,
        },
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: originalChartOrTableStep.incomingEdges[0].fromStep.id,
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            },
          ],
        },
        stepInput: {inputDataFrameId},
      },
      {
        originalStepId: originalChartOrTableStep.id,
      }
    );
  } else if (
    isReplacingStep &&
    pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalChartOrTableStep)
  ) {
    // In this case a user is updating a chart view step into a table view step.
    newStep = await addOrReplaceAndRunAnalysisStep(
      analysis.id,
      {
        stepType: PipelineStepType.TABLE_VIEW,
        stepVersion: 0,
        stepConfig: {viewName: originalChartOrTableStep.config?.chartName!},
        stepInputParents: {
          [SINGLE_DATASET_INPUT_STEP_DATA_NAME]: [
            {
              stepId: originalChartOrTableStep.incomingEdges[0].fromStep.id,
              stepDataName: SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
            },
          ],
        },
        stepInput: {inputDataFrameId},
      },
      {
        originalStepId: originalChartOrTableStep.id,
      }
    );
  } else if (
    pipelineStepIsStepType(PipelineStepType.CHART_VIEW, 0, originalChartOrTableStep) &&
    hasStepConfigChanged(originalChartOrTableStep, getChartConfig(newVisualizationConfig, newChartConfig))
  ) {
    // In this case a user is updating a chart view step with different chart config.
    await updateAndRunPipelineStep(analysis.id, {
      stepId: originalChartOrTableStep.id,
      stepConfig: {
        chartConfig: getChartConfig(newVisualizationConfig, newChartConfig),
        chartName: newChartConfig?.chartName,
      },
    });
  }

  if (isReplacingStep) {
    assert(newStep != null, 'newStep should not be null');
    // If we replaced the step, the step id changes so we need to update the main view router with the new view.
    onChangeSelectedItem({
      type: AnalysisMainViewItemType.DATA_VIEW,
      viewReference: {
        stepId: newStep.id,
        outputReference: {
          stepDataName:
            newStep.definition.stepType === PipelineStepType.CHART_VIEW
              ? SINGLE_CHART_OUTPUT_STEP_DATA_NAME
              : SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
          rawDataIndex: 0,
        },
      },
    });
  }
  onChangeSelectedRightSidebarItem(null);
}
